import type { ServerKeyPublic, Website } from 'types';
import type { StackedField } from '../../ConsolePanel';
import type { ServerKeysDialogManager } from './use-server-keys-dialog';
import { KeyRound } from 'lucide-react';
import { cn } from 'helpers/cn';
import { formatDateTime } from 'helpers/formatters';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/Dialogs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { GRID_TABLE_ROW, GRID_TABLE_ROWGROUP, GridTable, StackedRow } from '../../ConsolePanel';
import { useServerKeysDialog } from './use-server-keys-dialog';
import { SERVER_KEYS_COPY } from './server-keys-copy';

type ServerKeysModalProps = {
  website: Website;
  /** A product tour anchor for the row button. */
  tourAnchor?: string;
};

type ManagerProps = { manager: ServerKeysDialogManager };

const FIELD_CLASS = 'border-line bg-field text-foreground h-9 rounded-lg text-body';

const TEMPLATE = 'minmax(140px,1fr) minmax(130px,auto) repeat(3, minmax(120px,auto)) auto';

/**
 * A website's server keys, as a row dialog like ScriptsModal. The row renders this only for
 * someone who may manage keys (`server-keys:manage`). Creating and revoking both go through a
 * confirm that names the website and says what a key can do; a new key's secret is shown once.
 * The list and Revoke are always there; only the create form needs the website's visitor ID
 * on, and says so when it is off.
 */
export const ServerKeysModal = ({ website, tourAnchor }: ServerKeysModalProps) => {
  const manager = useServerKeysDialog(website);
  const { isOpen, open, close } = manager;
  const label = `${SERVER_KEYS_COPY.button} for ${website.url}`;

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        aria-label={label}
        title={label}
        onClick={open}
        data-tour={tourAnchor}
        className="text-dim hover:text-foreground hover:bg-hover h-8 w-8 rounded-lg"
      >
        <KeyRound className="h-4 w-4" />
      </Button>

      <Dialog open={isOpen} onOpenChange={(next) => !next && close()}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {SERVER_KEYS_COPY.title}: {website.url}
            </DialogTitle>
            <DialogDescription>
              {SERVER_KEYS_COPY.description} {SERVER_KEYS_COPY.salt}
            </DialogDescription>
          </DialogHeader>

          <div className="flex min-w-0 flex-col gap-4">
            {manager.secret && <SecretPanel manager={manager} />}
            <CreateForm manager={manager} />
            <KeysTable manager={manager} />
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={manager.confirmingCreate}
        title={SERVER_KEYS_COPY.createTitle(website.url)}
        confirmLabel={SERVER_KEYS_COPY.create}
        onConfirm={manager.confirmCreate}
        onCancel={manager.cancelCreate}
      >
        <p className="supporting m-0">{SERVER_KEYS_COPY.createBody}</p>
        <p className="supporting m-0 mt-2">{SERVER_KEYS_COPY.salt}</p>
      </ConfirmDialog>

      <ConfirmDialog
        open={!!manager.revoking}
        title={SERVER_KEYS_COPY.revokeTitle(manager.revoking?.name ?? '', website.url)}
        confirmLabel={SERVER_KEYS_COPY.revoke}
        confirmVariant="destructive"
        onConfirm={manager.confirmRevoke}
        onCancel={manager.cancelRevoke}
      >
        <p className="supporting m-0">{SERVER_KEYS_COPY.revokeBody}</p>
      </ConfirmDialog>
    </>
  );
};

const SecretPanel = ({ manager }: ManagerProps) => (
  <div className="border-sel-line bg-sel-bg rounded-card flex flex-col gap-2 border p-3.5">
    <div className="flex items-center justify-between gap-2">
      <span className="text-foreground text-body font-medium">{SERVER_KEYS_COPY.secretTitle}</span>
      <Button
        variant="ghost"
        size="sm"
        onClick={manager.copySecret}
        className="micro-label text-dim hover:text-foreground hover:bg-hover h-7 rounded-lg px-2"
      >
        {manager.copied ? SERVER_KEYS_COPY.copied : SERVER_KEYS_COPY.copy}
      </Button>
    </div>
    <pre className="bg-field border-line m-0 overflow-x-auto rounded-lg border p-3">
      <code className="log-line text-foreground break-all whitespace-pre-wrap">{manager.secret}</code>
    </pre>
    <p className="supporting m-0">
      {SERVER_KEYS_COPY.secretOnce} {SERVER_KEYS_COPY.secretActive}
    </p>
  </div>
);

const CreateForm = ({ manager }: ManagerProps) => {
  const { name, setName, nameError, canCreate, askCreate, visitorIdEnabled } = manager;
  const help = !visitorIdEnabled ? SERVER_KEYS_COPY.visitorIdOff : (nameError ?? SERVER_KEYS_COPY.nameHelp);

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={`server-key-name-${manager.website.id}`} className="micro-label">
        {SERVER_KEYS_COPY.nameLabel}
      </label>
      <div className="flex gap-2">
        <Input
          id={`server-key-name-${manager.website.id}`}
          value={name}
          onChange={(ev) => setName(ev.target.value)}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter') {
              ev.preventDefault();
              askCreate();
            }
          }}
          placeholder={SERVER_KEYS_COPY.namePlaceholder}
          disabled={!visitorIdEnabled}
          aria-invalid={!!nameError}
          className={FIELD_CLASS}
        />
        <Button onClick={askCreate} disabled={!canCreate} className="h-9 shrink-0 rounded-lg">
          {SERVER_KEYS_COPY.create}
        </Button>
      </div>
      <p className={cn('supporting m-0', visitorIdEnabled && nameError && 'text-danger')}>{help}</p>
    </div>
  );
};

const KeysTable = ({ manager }: ManagerProps) => {
  const { keys, isLoading, isError, retry, askRevoke } = manager;

  if (isLoading) return null;
  if (isError)
    return (
      <div className="flex items-center gap-2">
        <p className="supporting text-danger m-0">{SERVER_KEYS_COPY.loadFailed}</p>
        <Button
          variant="ghost"
          size="sm"
          onClick={retry}
          className="micro-label text-dim hover:text-foreground hover:bg-hover h-7 rounded-lg px-2"
        >
          {SERVER_KEYS_COPY.retry}
        </Button>
      </div>
    );
  if (keys.length === 0) return <p className="supporting m-0">{SERVER_KEYS_COPY.empty}</p>;

  const rows = keys.map((key) => ({ key, fields: fieldsFor(key) }));
  const columns = SERVER_KEYS_COPY.columns;

  const revokeButton = (key: ServerKeyPublic) =>
    key.revokedAt === null ? (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => askRevoke(key)}
        className="micro-label text-danger hover:bg-danger-bg hover:text-danger h-7 rounded-lg px-2"
      >
        {SERVER_KEYS_COPY.revoke}
      </Button>
    ) : null;

  return (
    <div className="border-line bg-panel2 rounded-card border">
      <div className="flex flex-col gap-2 p-3 md:hidden">
        {rows.map(({ key, fields }) => (
          <StackedRow
            key={key.id}
            className={cn(key.revokedAt !== null && 'opacity-60')}
            heading={<span className="text-foreground truncate text-body">{key.name}</span>}
            fields={fields}
          >
            <div className="mt-2 flex justify-end">{revokeButton(key)}</div>
          </StackedRow>
        ))}
      </div>

      <GridTable label={SERVER_KEYS_COPY.tableLabel} minWidth={720} template={TEMPLATE}>
        <div role="row" className={cn(GRID_TABLE_ROW, 'border-line items-center border-b py-2')}>
          <div role="columnheader" className="micro-label">
            {columns.name}
          </div>
          {rows[0]?.fields.map((field) => (
            <div key={field.key} role="columnheader" className="micro-label">
              {field.label}
            </div>
          ))}
          <div role="columnheader" className="micro-label">
            <span className="sr-only">{SERVER_KEYS_COPY.revoke}</span>
          </div>
        </div>
        <div role="rowgroup" className={GRID_TABLE_ROWGROUP}>
          {rows.map(({ key, fields }) => (
            <div
              key={key.id}
              role="row"
              className={cn(
                GRID_TABLE_ROW,
                'border-line items-center border-b py-2 last:border-b-0',
                key.revokedAt !== null && 'opacity-60',
              )}
            >
              <div role="cell" className="text-foreground truncate text-body" title={key.name}>
                {key.name}
              </div>
              {fields.map((field) => (
                <div key={field.key} role="cell" className={cn('text-meta', field.className)}>
                  {field.value}
                </div>
              ))}
              <div role="cell" className="flex justify-end">
                {revokeButton(key)}
              </div>
            </div>
          ))}
        </div>
      </GridTable>
    </div>
  );
};

function fieldsFor(key: ServerKeyPublic): StackedField[] {
  const columns = SERVER_KEYS_COPY.columns;
  return [
    { key: 'prefix', label: columns.prefix, value: `${key.keyPrefix}…`, className: 'font-mono text-micro text-dim' },
    { key: 'created', label: columns.created, value: formatDateTime(key.createdAt), className: 'num text-dim' },
    {
      key: 'lastUsed',
      label: columns.lastUsed,
      value: key.lastUsedAt ? formatDateTime(key.lastUsedAt) : SERVER_KEYS_COPY.never,
      className: 'num text-dim',
    },
    {
      key: 'status',
      label: columns.status,
      value: key.revokedAt ? SERVER_KEYS_COPY.revoked(formatDateTime(key.revokedAt)) : SERVER_KEYS_COPY.active,
      className: key.revokedAt ? 'text-dim' : 'text-ok',
    },
  ];
}
